import React, { Fragment, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Fuse from "fuse.js";

import Checkbox from 'material-ui/Checkbox';
import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow } from 'material-ui/Table';
import TextField from 'material-ui/TextField';
import IconButton from 'material-ui/IconButton';
import ChevronLeft from 'material-ui/svg-icons/navigation/chevron-left';
import ChevronRight from 'material-ui/svg-icons/navigation/chevron-right';
import { getUserList, addUsersFromAutocomplete } from 'actions/users';
import api from 'services';
import { findMembersOfCompany, getCompanies, findCompanyById, findCompany } from '../../../services/companies';

import styles from 'styles';
import Row from './Row';
import { AutoComplete } from 'material-ui';

const AutoCompleteWrapper = ({ fuse, updateUserListWithCompanyFilter, toggleFilterByCompanies, toggleRoleSetting }) => {
    const [autocompleteSource, setAutocompleteSource] = useState([]);
    const [value, setValue] = useState('');
    const [_id, setId] = useState(undefined);

    const handleAutocomplete = async (val) => {
        if (val.length < 2) return;

        const found = fuse.search(val);

        setValue(val);
        setAutocompleteSource(found);
    };

    const handleCompanySelect = async (selected) => {
        if (!selected._id) return;
        setId(selected._id);

        const companyMembers = await findMembersOfCompany({ _id: selected._id });

        updateUserListWithCompanyFilter(companyMembers);
        toggleFilterByCompanies(selected._id);
    };

    return (
        <AutoComplete 
            floatingLabelText="Search by company name"
            dataSource={autocompleteSource}
            dataSourceConfig={{text: 'name', value: '_id'}}
            onUpdateInput={handleAutocomplete}
            filter={AutoComplete.caseInsensitiveFilter}
            maxSearchResults={20}
            menuStyle={{maxHeight: '500px'}}
            onKeyDown={ (e) => {
                if (e.key === 'Enter') {
                    e.stopPropagation();
                    return false;
                }
            }}
            floatingLabelStyle={{
                fontSize: '13px'
            }}
            onNewRequest={(selected, e, r) => {
                handleCompanySelect(selected);
                toggleRoleSetting(value);
            }}
        />
    );
};
 
const TableUsersBan = ({ users: usersProp, getUserList, addUsersFromAutocomplete }) => {
    const [page, setPage] = useState(1);
    const [previous, setPrevious] = useState(null);
    const [next, setNext] = useState(null);
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [search, setSearch] = useState('');
    const [isDataFromAutoComplete, setIsDataFromAutoComplete] = useState(false);
    const [isFilteredByCompany, setIsFilteredByCompany] = useState(false);
    const [isSettingRoleEnabled, setIsSettingRoleEnabled] = useState(false);

    useEffect(() => {
        const fetchCompanies = async () => {
            const response = await getCompanies();
            setCompanies(response.data);
        };
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (usersProp) {
            setUsers(usersProp.data || []);
            setPage(usersProp.page || 1);
            setPrevious(usersProp.previous);
            setNext(usersProp.next);
            setIsDataFromAutoComplete(usersProp.isDataFromAutoComplete);
        }
    }, [usersProp]);

    const onChange = (e, val) => {
        setSearch(val);
        if (val === '') {
            getUserList({ page: 1 });
        }
    };

    const keyPress = (e) => {
        if (e.keyCode === 13) {
            if (search === '') return;
            
            let companyName; 
            if (isFilteredByCompany) companyName = search;

            api.findUser(search, true, companyName)
                .then((response) => {
                    addUsersFromAutocomplete(response.data);
                })
                .catch((response) => {
                    console.error(response);
                });
        }
    };

    const nextPage = () => {
        getUserList({
            page: next,
            limit: 25,
        });
        setIsFilteredByCompany(false);
    };

    const prevPage = () => {
        getUserList({
            page: previous,
            limit: 25,
        });
        setIsFilteredByCompany(false);
    };

    const updateUserListWithCompanyFilter = async (newUserListWithCompanyRoles) => {
        setUsers(newUserListWithCompanyRoles);
    };

    const updateCompanyMemberStatus = async (user) => {   
        const updatedUser = await api.findUserById(user);
        const updatedUsers = users.map(oldUser => {
            if (oldUser.user && oldUser.user._id === updatedUser._id) {
                oldUser.user.approved = updatedUser.approved;
                oldUser.role = updatedUser.role;
            }
            return oldUser;
        });

        setUsers(updatedUsers);
    };

    const toggleRoleSetting = (autocompleteSearchStr) => {
        if (autocompleteSearchStr.length < 2 || !autocompleteSearchStr.length) {
            setIsSettingRoleEnabled(false);
            return;
        }

        setIsSettingRoleEnabled(true);
    };

    const toggleCompanyRole = async (user) => {
        if (user.role === 'owner') {
            return;
        }
        if (user.role === 'pending') {
            return;
        }

        const responseBeforeUpdate = await findCompanyById(user.company._id);
        const companyUsers = responseBeforeUpdate.participants;

        if (user.role === 'manager') {
            companyUsers.forEach(userInCompany => {
                userInCompany.company = {_id: user.company._id};
                if (userInCompany.role !== 'pending') {
                    userInCompany.role = 'manager';
                    if (userInCompany._id === user._id) {
                        userInCompany.role = 'owner';
                    }
                }
            });
        }

        await api.setNewParticipantsRoles(companyUsers, user.company._id);
        const company = await findCompanyById(user.company._id);
        const updatedCompanyUsers = company.participants;

        updatedCompanyUsers.forEach(newUser => {
            newUser.company = {_id: company._id, name: company.name};
            newUser.user = {};
            users.forEach(oldUser => {
                if (oldUser._id === newUser._id) {
                    newUser.user._id = oldUser.user._id;
                    newUser.user.approved =  oldUser.user.approved;
                    newUser.user.emailVerified = oldUser.user.emailVerified;
                    newUser.user.createdAt = oldUser.user.createdAt;
                }
            });
        });
        
        updateUserListWithCompanyFilter(updatedCompanyUsers);
    };

    const toggleFilterByCompanies = (searchValue) => {
        if (!searchValue.length) {
            setIsFilteredByCompany(false);
            return;
        }

        setIsFilteredByCompany(true);
    };

    const fuse = new Fuse(companies, {
        threshold: 0,
        location: 0,
        distance: 10,
        maxPatternLength: 10,
        minMatchCharLength: 2,
        keys: ['name'],
    });

    return (
        <div>
            <div className='row'>
                <div className='col-xs-4 middle-xs' style={{ display: 'flex' }}>
                    <div style={{ width: '100%' }}>
                        <TextField
                            floatingLabelText='Search'
                            floatingLabelStyle={{ fontSize: '13px' }}
                            onChange={onChange}
                            onKeyDown={keyPress}
                            fullWidth
                        />
                    </div>
                </div>

                <div className='col-xs-4 middle-xs' style={{ display: 'flex' }}>
                    <AutoCompleteWrapper 
                        fuse={fuse}
                        toggleRoleSetting={toggleRoleSetting}
                        updateUserListWithCompanyFilter={updateUserListWithCompanyFilter}
                        toggleFilterByCompanies={toggleFilterByCompanies}
                    />
                </div> 
                
                <div className='col-xs-4'>
                    <div className=' center-xs middle-xs' style={{ width: '100%', display: 'flex' }}>
                        {
                            !isDataFromAutoComplete
                                ?
                                <Fragment>
                                    <IconButton
                                        iconStyle={styles.navigationLeft}
                                        name='navigationLeft'
                                        onClick={prevPage}
                                    >
                                        <ChevronLeft />
                                    </IconButton>
                                    <span>Page: {page}</span>
                                    <IconButton
                                        iconStyle={styles.navigationRight}
                                        name='navigationRight'
                                        onClick={nextPage}
                                    >
                                        <ChevronRight />
                                    </IconButton>
                                </Fragment>
                            : null
                        }
                    </div>
                </div>
            </div>

            <div className='table-body'>
                <Table selectable={false}>
                    <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                        <TableRow>
                            <TableHeaderColumn style={styles.tableHeaderColumnCSS}>
                                Name
                            </TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableRowColumnBigCSS}>
                                Email
                            </TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableHeaderColumnCSS}>
                                Role
                            </TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableHeaderColumnCSS}>
                                CreatedAt
                            </TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableHeaderColumnCSS}>
                                Company name
                            </TableHeaderColumn>
                            {isSettingRoleEnabled && isFilteredByCompany ? 
                                <TableHeaderColumn style={styles.tableHeaderColumnCSS}>
                                    Company role
                                </TableHeaderColumn>   :
                                null
                            }
                            <TableHeaderColumn style={styles.tableHeaderColumnSmallCSS}>
                                Email verification status
                            </TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableHeaderColumnSmallCSS}>
                                User approval
                            </TableHeaderColumn> 
                            <TableHeaderColumn style={styles.tableHeaderColumnSmallCSS}>
                                Sessions
                            </TableHeaderColumn>
                            <TableHeaderColumn style={styles.tableHeaderColumnCSS} />
                        </TableRow>
                    </TableHeader>

                    <TableBody displayRowCheckbox={false}>
                        {
                            users.map((item, index) => {
                                return (<Row
                                    key={item.user && item.user._id ||item._id}
                                    item={item}
                                    isFilteredByCompany={isFilteredByCompany}
                                    isVerifiedEmail={users[index] && users[index].emailVerified}
                                    isApproved={item.user && item.user.approved || item.approved}
                                    companyRoleMode={isSettingRoleEnabled}
                                    toggleCompanyRole={toggleCompanyRole}
                                    companyRole={item.role || 'unset'}
                                    updateCompanyMemberStatus={updateCompanyMemberStatus}
                                />);
                            })
                        }
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export default connect(
    ({ users }) => {
        return {
            users,
        };
    },
    dispatch => bindActionCreators({
            getUserList,
            addUsersFromAutocomplete,
    },
    dispatch),
)(TableUsersBan);



// WEBPACK FOOTER //
// ./app/components/Users/BanList/Table.jsx